import React from "react";
import { Trash2 } from "lucide-react";

import SelectDropdownV2 from "../../../components/ui/selectdropdown/v2";
import { Textarea } from "../../../components/ui/textbox/TextBox";
import { PrimaryAISparkleIcon } from "../../../components/ui/icons/PrimaryAISparkles";
import {
  FraudElementCategories,
  GeneratedFraudElement,
} from "../../../api/fraudElements";
import { CATEGORY_OPTIONS } from "./FraudElementFormData";

interface AIFraudElementCardProps {
  index: number;
  fraudElement: GeneratedFraudElement;
  onCategoryChange: (category: FraudElementCategories) => void;
  onDetailsChange: (details: string) => void;
  onDelete: () => void;
}

const AIFraudElementCard: React.FC<AIFraudElementCardProps> = ({
  index,
  fraudElement,
  onCategoryChange,
  onDetailsChange,
  onDelete,
}) => {
  const selectedCategory = CATEGORY_OPTIONS.find(
    (option) => option.value === fraudElement.fraud_element_category,
  );

  return (
    <div className="flex flex-col gap-4 rounded-[8px] bg-[#F9FAFB] p-3 font-[Inter]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PrimaryAISparkleIcon />

          <span className="text-[12px] font-medium leading-none text-[#4B5563]">
            Fraud Element {index + 1}
          </span>
        </div>

        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete fraud element ${index + 1}`}
          className="flex items-center justify-center text-[#0A0A0A] opacity-50 transition-opacity hover:opacity-100"
        >
          <Trash2 size={16} />
        </button>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex w-[330px] min-w-0 flex-col gap-2">
          <label className="text-sm font-medium leading-none text-[#0A0A0A]">
            Category<span className="text-[#DC2626]">*</span>
          </label>

          <SelectDropdownV2
            name="category"
            enableSearch
            options={CATEGORY_OPTIONS}
            selectedOption={selectedCategory}
            showTooltip={true}
            showOptionTooltip={true}
            onSelectionChange={(option) => onCategoryChange(option.value)}
            containerClassName="w-full min-w-0"
            labelClassName="h-[36px] py-2 px-3 border border-[#E5E5E5] bg-white rounded-[8px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] text-sm text-[#0A0A0A] focus:outline-none min-w-0 flex items-center justify-between overflow-hidden [&_div]:!w-full [&_div]:!min-w-0 [&_div]:!flex-1 [&_span]:!block [&_span]:!truncate [&_span]:!w-full"
            titleClassName="truncate block max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
            dropdownClassName="!w-[330px] !max-w-[330px] overflow-x-hidden"
            optionClassName="!whitespace-nowrap !truncate !block !w-full overflow-hidden text-ellipsis [&_span]:!inline-block [&_span]:!truncate [&_span]:!max-w-[85%] [&_span]:!overflow-hidden [&_span]:!text-ellipsis"
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium leading-none text-[#0A0A0A]">
            Details (Optional)
          </span>

          <Textarea
            value={fraudElement.details ?? ""}
            placeholder="Enter fraud element details..."
            rows={3}
            onChange={(event) => onDetailsChange(event.target.value)}
            style={{ resize: "none" }}
            className="h-[76px] py-2 px-3 border border-[#E5E5E5] bg-white rounded-[8px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] text-sm text-[#0A0A0A] placeholder:text-[#737373] placeholder:leading-5 focus:outline-none focus:ring-0 focus:ring-transparent focus:border-[#E5E5E5]"
          />
        </div>
      </div>
    </div>
  );
};

export default AIFraudElementCard;